import { Controller } from 'stimulus';
import { FetchRequest } from '@rails/request.js';
import { show as showToast } from '@/js/application_controllers/ui/toast_controller';
import { openCollection } from './templates/collection_opener_controller';
import { useEventListener } from '../../util';

const LOADING_CLASS = 'is-loading';

export default class extends Controller {
  static targets = ['button', 'successToast', 'errorToast'];

  static values = {
    url: String,
    resetUrl: String,
  };

  connect() {
    useEventListener(this);
    document.addEventListener('click', this.#handleViewCollectionClick.bind(this));
  }

  #handleViewCollectionClick(e) {
    const button = e.target.closest('[data-role="view-collection"]');
    if (!button?.dataset.collectionId || button.disabled) {
      return;
    }

    // Multiple controller instances can coexist since slides are kept in the DOM
    // when hidden. Only the visible instance should handle the click.
    if (this.element.offsetParent === null) {
      return;
    }

    this.#closeCurrentSlider();
    this.#switchToMyTemplatesTab();
    openCollection(button.dataset.collectionId);
  }

  #closeCurrentSlider() {
    const slider = this.element.closest('[data-moodboard--slider-active]');
    slider.removeAttribute('data-moodboard--slider-active');
  }

  #switchToMyTemplatesTab() {
    const navButton = document.querySelector(
      '[data-moodboard--navigation-target="item"][value="private"]'
    );
    if (navButton && !navButton.classList.contains('is-active')) {
      navButton.click();
    }
  }

  async save() {
    if (this.buttonTarget.classList.contains(LOADING_CLASS)) {
      return;
    }

    this.buttonTarget.classList.add(LOADING_CLASS);

    try {
      const request = new FetchRequest('post', this.urlValue, {
        responseKind: 'json',
      });
      const response = await request.perform();

      if (response.statusCode === 201) {
        const data = await response.json;
        this.#setupViewButton(data.id);
        showToast(this.successToastTarget);
        this.#resetMyTemplatesList();
      } else {
        showToast(this.errorToastTarget);
      }
    } catch {
      showToast(this.errorToastTarget);
    } finally {
      this.buttonTarget.classList.remove(LOADING_CLASS);
    }
  }

  #setupViewButton(collectionId) {
    const button = this.successToastTarget.content.querySelector('[data-role="view-collection"]');
    if (!button) {
      return;
    }

    button.dataset.collectionId = collectionId;
    button.disabled = true;

    const spinner = button.querySelector('[data-role="view-collection-spinner"]');
    if (spinner) {
      spinner.classList.remove('hide');
    }
  }

  #resetMyTemplatesList() {
    const container = document.getElementById('sidepanel_templates_private');
    const loader = document.getElementById('sidepanel_templates_private-next');

    if (!container || !loader) {
      return;
    }

    container.innerHTML = '';
    loader.innerHTML = '';
    loader.removeAttribute('complete');
    loader.src = this.resetUrlValue;
    loader.setAttribute('loading', 'eager');

    const observer = new MutationObserver(() => {
      observer.disconnect();
      loader.setAttribute('loading', 'lazy');

      const viewButton = document.querySelector(
        '[data-role="view-collection"][data-collection-id]'
      );
      if (viewButton) {
        viewButton.disabled = false;

        const spinner = viewButton.querySelector('[data-role="view-collection-spinner"]');
        if (spinner) {
          spinner.classList.add('hide');
        }
      }
    });

    observer.observe(container, { childList: true });
  }
}
